<template>
  <div class="e3-tip-wrap" style="display: inline-block">
    <!-- Trigger -->
    <span
      ref="trigger"
      class="items-center"
      :aria-describedby="ariaId"
      :aria-disabled="disabled ? 'true' : 'false'"
      @mouseenter="triggerType === 'hover' && onEnter($event)"
      @mouseleave="triggerType === 'hover' && onLeave()"
      @click="triggerType === 'click' && !disabled && toggle($event)"
      @focus="triggerType === 'focus' && onEnter($event)"
      @blur="triggerType === 'focus' && onLeave()"
    >
      <slot />
    </span>

    <!-- Tooltip -->
    <transition name="e3-tip-fade">
      <div
        v-show="visible"
        ref="panel"
        class="e3-tip-panel"
        :class="[`e3-tip--${actualPlacement}`, customClass]"
        :style="panelStyleComputed"
        role="tooltip"
        :id="ariaId"
        @mouseenter="triggerType === 'hover' && cancelHide()"
        @mouseleave="triggerType === 'hover' && scheduleHide()"
      >
        <div class="e3-tip">
          <div v-if="title" class="e3-tip__title">{{ title }}</div>
          <hr v-if="title" class="e3-tip__divider" />
          <div class="e3-tip__content">
            <slot name="content">{{ text }}</slot>
          </div>
        </div>
        <div class="e3-tip__arrow" />
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onBeforeUnmount, nextTick } from "vue";

export default {
  name: "CustomTooltip",
  props: {
    title: String,
    text: String,
    placement: { type: String, default: "right" },
    width: { type: [Number, String], default: 260 },
    triggerType: { type: String, default: "hover" },
    disabled: { type: Boolean, default: false },
    delayIn: { type: Number, default: 120 },
    delayOut: { type: Number, default: 80 },
    offset: { type: Number, default: 10 },
    showCloseIcon: { type: Boolean, default: false },
    fixed: { type: Boolean, default: false },
    customClass: { type: String, default: "" },
    zIndex: { type: Number, default: 1000 },
  },
  setup(props, { emit }) {
    const trigger = ref(null);
    const panel = ref(null);
    const visible = ref(false);
    const ariaId = `e3-native-tip-${Math.random().toString(36).slice(2)}`;
    const timers = reactive({ in: null, out: null });
    const actualPlacement = ref(props.placement);

    const panelStyleComputed = computed(() => ({
      width: typeof props.width === "number" ? `${props.width}px` : props.width,
      position: props.fixed ? "fixed" : "absolute",
      top: `${state.top}px`,
      left: `${state.left}px`,
      zIndex: props.zIndex,
    }));

    const state = reactive({ top: -9999, left: -9999 });

    function measureAndPlace() {
      if (!trigger.value || !panel.value) return;
      const tRect = trigger.value.getBoundingClientRect();
      const pRect = panel.value.getBoundingClientRect();

      const scrollX = props.fixed ? 0 : window.pageXOffset;
      const scrollY = props.fixed ? 0 : window.pageYOffset;

      let top = 0;
      let left = 0;
      let chosen = props.placement;
      const gap = props.offset;

      const fits = {
        top: tRect.top - pRect.height - gap > 4,
        bottom: tRect.bottom + pRect.height + gap < window.innerHeight - 4,
        left: tRect.left - pRect.width - gap > 4,
        right: tRect.right + pRect.width + gap < window.innerWidth - 4,
      };

      function place(where) {
        switch (where) {
          case "top":
            top = (props.fixed ? 0 : scrollY) + tRect.top - pRect.height - gap;
            left = (props.fixed ? 0 : scrollX) + tRect.left + tRect.width / 2 - pRect.width / 2;
            break;
          case "bottom":
            top = (props.fixed ? 0 : scrollY) + tRect.bottom + gap;
            left = (props.fixed ? 0 : scrollX) + tRect.left + tRect.width / 2 - pRect.width / 2;
            break;
          case "left":
            top = (props.fixed ? 0 : scrollY) + tRect.top + tRect.height / 2 - pRect.height / 2;
            left = (props.fixed ? 0 : scrollX) + tRect.left - pRect.width - gap;
            break;
          case "right":
          default:
            top = (props.fixed ? 0 : scrollY) + tRect.top + tRect.height / 2 - pRect.height / 2;
            left = (props.fixed ? 0 : scrollX) + tRect.right + gap;
        }
      }

      const order = {
        top: ["top", "right", "left", "bottom"],
        bottom: ["bottom", "right", "left", "top"],
        left: ["left", "top", "bottom", "right"],
        right: ["right", "top", "bottom", "left"],
      }[props.placement];

      let placed = false;
      for (const w of order) {
        if (fits[w]) {
          chosen = w;
          place(w);
          placed = true;
          break;
        }
      }
      if (!placed) {
        chosen = "bottom";
        place("bottom");
      }

      left = Math.max(
        (props.fixed ? 0 : scrollX) + 4,
        Math.min(left, (props.fixed ? 0 : scrollX) + window.innerWidth - pRect.width - 4)
      );

      state.top = top;
      state.left = left;
      actualPlacement.value = chosen;
    }

    function show(e) {
      if (props.disabled || visible.value) return;
      visible.value = true;
      emit("show");
      nextTick(measureAndPlace);
      addGlobalListeners();
    }

    function hide() {
      if (!visible.value) return;
      visible.value = false;
      emit("hide");
      removeGlobalListeners();
    }

    function toggle(e) {
      visible.value ? hide() : show(e);
    }

    function clearTimers() {
      if (timers.in) {
        clearTimeout(timers.in);
        timers.in = null;
      }
      if (timers.out) {
        clearTimeout(timers.out);
        timers.out = null;
      }
    }
    function onEnter(e) {
      clearTimers();
      timers.in = setTimeout(() => show(e), props.delayIn);
    }
    function onLeave() {
      clearTimers();
      timers.out = setTimeout(hide, props.delayOut);
    }
    function scheduleHide() {
      onLeave();
    }
    function cancelHide() {
      clearTimers();
    }

    function onKeyDown(e) {
      if (e.key === "Escape") hide();
    }
    function onScrollOrResize() {
      if (visible.value) measureAndPlace();
    }

    function addGlobalListeners() {
      document.addEventListener("keydown", onKeyDown);
      window.addEventListener("scroll", onScrollOrResize, true);
      window.addEventListener("resize", onScrollOrResize);
      document.addEventListener("click", onOutsideClick, true);
    }
    function removeGlobalListeners() {
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
      document.removeEventListener("click", onOutsideClick, true);
    }
    function onOutsideClick(e) {
      if (!panel.value || !trigger.value) return;
      if (panel.value.contains(e.target) || trigger.value.contains(e.target)) return;
      hide();
    }

    onMounted(() => {
      if (panel.value && panel.value.parentNode !== document.body) {
        document.body.appendChild(panel.value);
      }
    });
    onBeforeUnmount(() => {
      clearTimers();
      removeGlobalListeners();
      if (panel.value && panel.value.parentNode === document.body)
        document.body.removeChild(panel.value);
    });

    return {
      trigger,
      panel,
      visible,
      ariaId,
      actualPlacement,
      panelStyleComputed,
      toggle,
      show,
      hide,
      onEnter,
      onLeave,
      cancelHide,
      scheduleHide,
    };
  },
};
</script>

<style scoped>
.e3-tip-wrap {
  display: block !important;
  width: 100%;
}

.e3-tip-panel {
  position: absolute;
  inset: auto;
  pointer-events: auto;
  --e3-tip-bg: #fff; /* color de fondo compartido con la flecha */
}

.e3-tip-fade-enter-active,
.e3-tip-fade-leave-active {
  transition: opacity 0.12s ease, transform 0.12s ease;
}
.e3-tip-fade-enter,
.e3-tip-fade-leave-to {
  opacity: 0;
  transform: translateY(-2px);
}

.e3-tip {
  position: relative;
  background: var(--e3-tip-bg, #fff);
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0px 5px 12px rgba(0, 0, 0, 0.3); /* tomado de Figma */
  padding: 16px 18px;
  width: 311px;
  height: fit-content;
  max-width: 90vw;
  overflow: visible;
}

.e3-tip__title {
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 18px;
  line-height: 26px;
  letter-spacing: 0;
  text-align: center;
  margin-bottom: 8px;
  color: var(--e3-tip-title, #111827);
}

.e3-tip__divider {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 8px -18px;
  width: calc(100% + 36px);
}

.e3-tip__content {
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: 0;
  color: #6b7280; /* gris suave */
  text-align: center;
}
.e3-tip__close {
  position: absolute;
  right: 6px;
  top: 4px;
  border: none;
  background: transparent;
  font-size: 16px;
  cursor: pointer;
  line-height: 1;
  color: #6b7280;
}

/* ---------- Flecha: borde mayor (8px) + relleno (7px) ---------- */
.e3-tip__arrow {
  position: absolute;
  width: 0;
  height: 0;
  pointer-events: none;
  z-index: 1; /* sobre el borde del panel */
}

/* BOTTOM (tooltip debajo → flecha arriba) */
/* Flecha inferior (bottom) */
.e3-tip--bottom .e3-tip__arrow {
  top: -8px;
  left: 50%;
  transform: translateX(-50%); /* centra perfecto sin importar el ancho */
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-bottom: 8px solid #e5e7eb; /* borde gris del tooltip */
}

.e3-tip--bottom .e3-tip__arrow::after {
  content: "";
  position: absolute;
  left: -7px;
  top: 1px; /* baja 1px para tapar la línea del borde */
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-bottom: 7px solid var(--e3-tip-bg, #fff);
}
/* TOP (tooltip arriba → flecha abajo) */
.e3-tip--top .e3-tip__arrow {
  bottom: -8px;
  left: calc(50% - 8px);
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 8px solid var(--e3-tip-border, #c7b6ff);
}
.e3-tip--top .e3-tip__arrow::after {
  content: "";
  position: absolute;
  left: -7px;
  top: -8px; /* alinea interior */
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-top: 7px solid var(--e3-tip-bg);
}

/* RIGHT (tooltip a la derecha → flecha a la izquierda) */
.e3-tip--right .e3-tip__arrow {
  left: -8px;
  top: 50%;
  transform: translateY(-50%);
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-right: 8px solid var(--e3-tip-border, #c7b6ff);
}
.e3-tip--right .e3-tip__arrow::after {
  content: "";
  position: absolute;
  left: 1px;
  top: -7px; /* cubre la junta 1px */
  border-top: 7px solid transparent;
  border-bottom: 7px solid transparent;
  border-right: 7px solid var(--e3-tip-bg);
}

/* LEFT (tooltip a la izquierda → flecha a la derecha) */
.e3-tip--left .e3-tip__arrow {
  right: -8px;
  top: 50%;
  transform: translateY(-50%);
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-left: 8px solid var(--e3-tip-border, #c7b6ff);
}
.e3-tip--left .e3-tip__arrow::after {
  content: "";
  position: absolute;
  right: 1px;
  top: -7px; /* cubre la junta 1px */
  border-top: 7px solid transparent;
  border-bottom: 7px solid transparent;
  border-left: 7px solid var(--e3-tip-bg);
}
</style>
